/**
 * Révèle — Le Révélateur
 * Référence : docs/02-ARCHITECTURE.md §5
 *
 * Deux façons d'arriver à un parfum :
 *
 *   1. PAR SON NOM. Le client nomme un parfum qu'il aime, et la recherche descend
 *      une cascade, dans cet ordre :
 *        · couvert par une création EN VENTE → nos correspondances ;
 *        · couvert par une création du catalogue PAS ENCORE EN VENTE → annonce de
 *          l'arrivée, alerte par e-mail, et en attendant les plus proches disponibles ;
 *        · parfum connu HORS catalogue → les créations disponibles les plus proches ;
 *        · parfum inconnu → le questionnaire.
 *      Le client ne tombe jamais sur une impasse.
 *
 *   2. PAR QUESTIONS. Cinq questions, puis une sélection de un à trois parfums
 *      disponibles, chacun avec son affinité et la raison du choix.
 *
 * L'index (`index-olfactif.json`) est chargé au PREMIER CLIC, jamais au
 * chargement de la page : aucun impact sur le LCP. Recherche et questionnaire
 * s'exécutent intégralement en local.
 *
 * API publique :
 *   window.ReveleFinder.open(prefill?, mode?)       mode : 'matchmaker'
 *   document.dispatchEvent(new CustomEvent('revele:finder:open', { detail: { prefill, mode } }))
 *
 * Évènements émis (pour l'analytique) :
 *   rv_finder_open · rv_finder_search · rv_finder_select · rv_finder_no_result
 *   rv_finder_add_to_cart · rv_finder_notify · rv_matchmaker_start · rv_matchmaker_result
 *
 * `rv_finder_no_result` et `rv_finder_notify` sont les métriques les plus utiles du
 * site : les parfums que les clients cherchent et que la collection ne couvre pas.
 */

const RANGS = {
  primary: 'match_primary',
  secondary: 'match_secondary',
  tertiary: 'match_tertiary',
};

/** Minuscules, sans accents, sans ponctuation — miroir du script de build. */
function normaliser(s) {
  return String(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Distance de Levenshtein bornée : on abandonne dès que le seuil est dépassé,
 * ce qui évite de calculer la matrice complète pour des chaînes très éloignées.
 */
function distance(a, b, max) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let precedente = Array.from({ length: b.length + 1 }, (_, i) => i);
  let courante = new Array(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    courante[0] = i;
    let minLigne = i;
    for (let j = 1; j <= b.length; j++) {
      const cout = a[i - 1] === b[j - 1] ? 0 : 1;
      courante[j] = Math.min(courante[j - 1] + 1, precedente[j] + 1, precedente[j - 1] + cout);
      if (courante[j] < minLigne) minLigne = courante[j];
    }
    if (minLigne > max) return max + 1;
    [precedente, courante] = [courante, precedente];
  }
  return precedente[b.length];
}

/** Correspondance tolérante aux fautes de frappe. */
function ressemble(requete, cible) {
  if (cible.includes(requete)) return true;
  if (requete.length < 4) return false;
  const seuil = requete.length <= 6 ? 1 : 2;
  for (const mot of cible.split(' ')) {
    if (Math.abs(mot.length - requete.length) <= seuil + 1) {
      if (distance(requete, mot, seuil) <= seuil) return true;
    }
  }
  return distance(requete, cible, seuil) <= seuil;
}

/* ── Questionnaire ───────────────────────────────────────────────────────── */

/**
 * Parenté entre familles, miroir de scripts/build-index-olfactif.mjs. Une famille
 * que la collection disponible ne couvre pas encore trouve ainsi son voisin le plus
 * plausible.
 */
const PARENTE = {
  Gourmand: { Oriental: 0.6, Floral: 0.4, Boisé: 0.2, Frais: 0.1, Chypré: 0.2 },
  Oriental: { Gourmand: 0.6, Boisé: 0.5, Floral: 0.3, Chypré: 0.4, Frais: 0.1 },
  Boisé: { Oriental: 0.5, Chypré: 0.6, Frais: 0.5, Gourmand: 0.2, Floral: 0.2 },
  Floral: { Gourmand: 0.4, Chypré: 0.5, Frais: 0.4, Oriental: 0.3, Boisé: 0.2 },
  Frais: { Boisé: 0.5, Floral: 0.4, Chypré: 0.3, Oriental: 0.1, Gourmand: 0.1 },
  Chypré: { Boisé: 0.6, Floral: 0.5, Oriental: 0.4, Frais: 0.3, Gourmand: 0.2 },
};
function parente(a, b) {
  if (!a || !b) return 0.3;
  return a === b ? 1 : PARENTE[a]?.[b] ?? 0;
}

/**
 * Les questions. Les libellés viennent des traductions (`q_<id>`, `o_<option>`,
 * `d_<option>`) ; seules la structure et la logique vivent ici.
 *   max  — nombre de choix autorisés (Infinity : autant que voulu)
 *   min  — 0 pour une question facultative
 */
const QUESTIONS = [
  { id: 'univers', aide: 'one_or_two', min: 1, max: 2, options: ['floral', 'gourmand', 'oriental', 'boise', 'frais', 'chypre'] },
  { id: 'genre', aide: 'one', min: 1, max: 1, options: ['feminin', 'masculin', 'libre'] },
  { id: 'moment', aide: 'one_or_two', min: 1, max: 2, options: ['jour', 'soir', 'occasion'] },
  { id: 'presence', aide: 'one', min: 1, max: 1, options: ['discret', 'affirme', 'intense'] },
  { id: 'eviter', aide: 'many', min: 0, max: Infinity, options: ['sucre', 'fleuri', 'epice', 'rien'], exclusive: 'rien' },
];

const FAMILLE_DE = { floral: 'Floral', gourmand: 'Gourmand', oriental: 'Oriental', boise: 'Boisé', frais: 'Frais', chypre: 'Chypré' };
const MOMENTS_DE = { jour: ['Journée', 'Bureau', 'Quotidien'], soir: ['Soirée'], occasion: ['Occasion spéciale'] };
const INTENSITE_DE = { discret: 3, affirme: 4, intense: 5 };
const EXCLUSION_DE = { sucre: 'Gourmand', fleuri: 'Floral', epice: 'Oriental' };

/**
 * Affinité d'une création avec les réponses, de 0 à 100.
 *   0,45 famille · 0,20 genre · 0,15 moment · 0,20 présence
 * La famille pèse le plus : c'est le critère que le client ressent en premier.
 */
function affinite(produit, rep) {
  const familles = rep.univers.map((u) => FAMILLE_DE[u]);
  const famille = Math.max(...familles.map((f) => parente(f, produit.f)));

  let genre = 1;
  if (rep.genre[0] === 'feminin') genre = { Femme: 1, Mixte: 0.75, Homme: 0 }[produit.g] ?? 0.5;
  if (rep.genre[0] === 'masculin') genre = { Homme: 1, Mixte: 0.75, Femme: 0 }[produit.g] ?? 0.5;

  const voulus = rep.moment.flatMap((m) => MOMENTS_DE[m]);
  const moment = (produit.mo ?? []).some((m) => voulus.includes(m)) ? 1 : 0.35;

  const cible = INTENSITE_DE[rep.presence[0]];
  const presence = produit.i ? Math.max(0, 1 - Math.abs(produit.i - cible) / 2) : 0.5;

  return Math.round((0.45 * famille + 0.2 * genre + 0.15 * moment + 0.2 * presence) * 100);
}

class ReveleFinder extends HTMLElement {
  connectedCallback() {
    this.index = null;
    this.chargement = null;
    this.suggestionActive = -1;
    this.suggestions = [];
    this.quiz = null;

    this.indexUrl = this.dataset.indexUrl;
    this.strings = JSON.parse(this.querySelector('[data-rv-finder-strings]').textContent);

    this.trigger = document.querySelector('.rv-finder-trigger');
    this.panel = this.querySelector('.rv-finder');
    this.scrim = this.querySelector('.rv-finder-scrim');
    this.input = this.querySelector('.rv-finder__input');
    this.clear = this.querySelector('.rv-finder__clear');
    this.body = this.querySelector('.rv-finder__body');
    this.closeBtn = this.querySelector('.rv-finder__close');

    this.trigger?.addEventListener('click', () => this.ouvrir());
    this.closeBtn.addEventListener('click', () => this.fermer());
    this.scrim.addEventListener('click', () => this.fermer());

    this.input.addEventListener('input', this.onSaisie.bind(this));
    this.input.addEventListener('keydown', this.onTouche.bind(this));
    this.clear.addEventListener('click', () => this.viderChamp());

    this.body.addEventListener('click', this.onClicCorps.bind(this));
    this.body.addEventListener('submit', this.onEnvoiAlerte.bind(this));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.panel.hidden) this.fermer();
    });

    document.addEventListener('revele:finder:open', (e) => this.ouvrir(e.detail?.prefill, e.detail?.mode));

    window.ReveleFinder = {
      open: (prefill, mode) => this.ouvrir(prefill, mode),
      close: () => this.fermer(),
    };
  }

  /* ── Ouverture et fermeture ─────────────────────────────────────────── */

  async ouvrir(prefill, mode) {
    this.declencheur = document.activeElement;

    this.panel.hidden = false;
    this.scrim.hidden = false;
    this.panel.classList.add('rv-finder--entering');
    this.trigger?.setAttribute('aria-expanded', 'true');

    if (window.matchMedia('(max-width: 767px)').matches) {
      document.body.style.overflow = 'hidden';
    }

    this.emettre('rv_finder_open', { mode: mode ?? 'search' });
    this.piegerFocus();

    if (mode !== 'matchmaker') requestAnimationFrame(() => this.input.focus());

    try {
      await this.chargerIndex();
    } catch {
      return;
    }

    if (mode === 'matchmaker') {
      this.demarrerQuiz();
      return;
    }

    // Restaure la dernière recherche, ou applique la valeur pré-remplie
    const memorise = prefill || sessionStorage.getItem('rv-finder-query');
    if (memorise) {
      this.input.value = memorise;
      this.clear.hidden = false;
      this.rechercher(memorise);
    } else {
      this.afficherAccueil();
    }
  }

  fermer() {
    this.panel.hidden = true;
    this.scrim.hidden = true;
    this.panel.classList.remove('rv-finder--entering');
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.libererFocus();

    // Le focus retourne d'où il venait
    if (this.declencheur?.isConnected) this.declencheur.focus();
    else this.trigger?.focus();
  }

  /* ── Piège de focus ─────────────────────────────────────────────────── */

  piegerFocus() {
    this.gestionTab = (e) => {
      if (e.key !== 'Tab') return;
      const cibles = this.panel.querySelectorAll(
        'button:not([disabled]), input, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!cibles.length) return;
      const premier = cibles[0];
      const dernier = cibles[cibles.length - 1];

      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };
    this.panel.addEventListener('keydown', this.gestionTab);
  }

  libererFocus() {
    if (this.gestionTab) this.panel.removeEventListener('keydown', this.gestionTab);
  }

  /* ── Index ──────────────────────────────────────────────────────────── */

  chargerIndex() {
    if (this.index) return Promise.resolve(this.index);
    if (this.chargement) return this.chargement;

    this.afficherChargement();

    this.chargement = fetch(this.indexUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        this.index = data;
        return data;
      })
      .catch((e) => {
        console.error('[Révèle] Index olfactif :', e);
        this.chargement = null;
        this.body.innerHTML = `<p class="rv-finder__empty">${this.strings.loadError}</p>`;
        throw e;
      });

    return this.chargement;
  }

  /* ── Saisie ─────────────────────────────────────────────────────────── */

  onSaisie() {
    const valeur = this.input.value;
    this.clear.hidden = valeur === '';
    this.quiz = null;

    clearTimeout(this.debounce);
    this.debounce = setTimeout(async () => {
      if (valeur.trim().length < 2) {
        this.afficherAccueil();
        return;
      }
      // Saisie plus rapide que le chargement de l'index : on l'attend, sinon la
      // recherche conclurait à tort « Nous ne connaissons pas encore ce parfum ».
      if (!this.index) {
        try {
          await this.chargerIndex();
        } catch {
          return;
        }
        if (this.input.value !== valeur) return; // une saisie plus récente a pris la main
      }
      this.suggerer(valeur);
    }, 180);
  }

  onTouche(e) {
    if (!this.suggestions.length) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      this.suggestionActive =
        (this.suggestionActive + delta + this.suggestions.length) % this.suggestions.length;
      this.majSuggestionActive();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const choix = this.suggestions[this.suggestionActive >= 0 ? this.suggestionActive : 0];
      if (choix) this.selectionner(choix.id);
    }
  }

  viderChamp() {
    this.input.value = '';
    this.clear.hidden = true;
    sessionStorage.removeItem('rv-finder-query');
    this.afficherAccueil();
    this.input.focus();
  }

  /* ── Recherche ──────────────────────────────────────────────────────── */

  /**
   * Classe les références qui répondent à la saisie. À pertinence égale, un parfum
   * en vente passe avant un parfum à venir, qui passe avant un parfum hors catalogue :
   * « sauvage » doit proposer d'abord ce que l'on peut acheter.
   */
  trouver(requete) {
    const q = normaliser(requete);
    if (!q || !this.index) return [];

    const priorite = { live: 2, soon: 1, ext: 0 };
    const scores = [];
    for (const ref of this.index.refs) {
      let meilleur = 0;
      for (const alias of ref.a) {
        if (alias === q) { meilleur = 100; break; }
        if (alias.startsWith(q)) { meilleur = Math.max(meilleur, 90); continue; }
        if (alias.includes(q)) { meilleur = Math.max(meilleur, 70); continue; }
        if (ressemble(q, alias)) meilleur = Math.max(meilleur, 50);
      }
      if (meilleur > 0) scores.push({ ...ref, _score: meilleur });
    }

    return scores
      .sort((a, b) => b._score - a._score || (priorite[b.st] ?? 0) - (priorite[a.st] ?? 0))
      .slice(0, 8);
  }

  suggerer(requete) {
    this.suggestions = this.trouver(requete);
    this.suggestionActive = -1;
    this.emettre('rv_finder_search', { query: requete, results: this.suggestions.length });

    if (!this.suggestions.length) {
      this.emettre('rv_finder_no_result', { query: requete });
      this.afficherAucunResultat(requete);
      return;
    }

    this.input.setAttribute('aria-expanded', 'true');
    this.body.innerHTML = `
      <ul class="rv-finder__suggestions" role="listbox" id="rv-finder-listbox">
        ${this.suggestions
          .map(
            (s, i) => `
          <li role="presentation">
            <button type="button" class="rv-finder__suggestion" role="option"
                    id="rv-sugg-${i}" aria-selected="false" data-ref="${this.esc(s.id)}">
              <span>${this.esc(s.n)}</span>
              ${s.st === 'soon' ? `<span class="rv-finder__suggestion-tag">${this.esc(this.strings.tagSoon)}</span>` : ''}
            </button>
          </li>`
          )
          .join('')}
      </ul>`;
  }

  majSuggestionActive() {
    const options = this.body.querySelectorAll('[role="option"]');
    options.forEach((o, i) => {
      const actif = i === this.suggestionActive;
      o.setAttribute('aria-selected', String(actif));
      if (actif) {
        this.input.setAttribute('aria-activedescendant', o.id);
        o.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  selectionner(refId) {
    const ref = this.index.refs.find((r) => r.id === refId);
    if (!ref) return;

    // Nom du modèle seul : aucune maison n'est affichée par le Révélateur
    const libelle = ref.n;
    this.input.value = libelle;
    this.clear.hidden = false;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    sessionStorage.setItem('rv-finder-query', libelle);

    this.emettre('rv_finder_select', { reference: ref.n, status: ref.st });
    this.afficherResultat(ref);
  }

  rechercher(valeur) {
    const trouves = this.trouver(valeur);
    if (trouves.length === 1 || (trouves.length && normaliser(valeur).includes(normaliser(trouves[0].n)))) {
      this.afficherResultat(trouves[0]);
    } else if (trouves.length) {
      this.suggerer(valeur);
    } else {
      this.afficherAucunResultat(valeur);
    }
  }

  /* ── Affichages : recherche ─────────────────────────────────────────── */

  afficherChargement() {
    this.body.innerHTML = `
      <div class="rv-finder__loading">
        <span class="rv-finder__spinner" aria-hidden="true"></span>
        <span>${this.strings.loading}</span>
      </div>`;
  }

  famillesHtml() {
    const familles = [...new Set(
      Object.values(this.index?.products ?? {}).map((p) => p.f).filter(Boolean)
    )].sort();
    if (!familles.length) return '';
    return `<div class="rv-finder__families">
      ${familles
        .map((f) => `<button type="button" class="rv-finder__family" data-famille="${this.esc(f)}">${this.esc(f)}</button>`)
        .join('')}
    </div>`;
  }

  boutonQuizHtml(libelle, fantome = false) {
    return `<button type="button" class="rv-finder__cta${fantome ? ' rv-finder__cta--ghost' : ''}" data-quiz-start>
      ${this.esc(libelle)}
    </button>`;
  }

  afficherAccueil() {
    this.input.setAttribute('aria-expanded', 'false');
    this.suggestions = [];

    this.body.innerHTML = `
      <p class="rv-finder__intro-title">${this.strings.onboardingTitle}</p>
      <ol class="rv-finder__steps">
        <li class="rv-finder__step">${this.strings.step1}</li>
        <li class="rv-finder__step">${this.strings.step2}</li>
        <li class="rv-finder__step">${this.strings.step3}</li>
      </ol>
      <div class="rv-finder__quiz-entry">
        <p class="rv-finder__or">${this.esc(this.strings.homeQuiz)}</p>
        ${this.boutonQuizHtml(this.strings.startQuiz)}
      </div>
      <p class="rv-finder__or">${this.strings.orBrowse}</p>
      ${this.famillesHtml()}`;
  }

  /**
   * Dernier étage de la cascade : le parfum n'est ni dans la collection ni parmi les
   * parfums connus. Le questionnaire prend le relais plutôt qu'un simple « désolé ».
   */
  afficherAucunResultat(requete = '') {
    this.input.setAttribute('aria-expanded', 'false');
    this.suggestions = [];

    this.body.innerHTML = `
      <div class="rv-finder__notice">
        <p class="rv-finder__notice-title">${this.esc(this.strings.noMatch.replace('__QUERY__', requete.trim()))}</p>
        <p class="rv-finder__notice-text">${this.esc(this.strings.noMatchHelp)}</p>
        ${this.boutonQuizHtml(this.strings.startQuiz)}
      </div>
      <p class="rv-finder__or">${this.strings.orBrowse}</p>
      ${this.famillesHtml()}`;
  }

  afficherResultat(ref) {
    this.quiz = null;
    if (ref.st === 'soon') return this.afficherBientot(ref);
    if (ref.st === 'ext') return this.afficherHorsCatalogue(ref);

    const cartes = this.cartesDe(ref.m);
    this.body.innerHTML = `
      <p class="rv-finder__section-title">${this.strings.ourMatches}</p>
      ${this.grilleHtml(cartes, (m) => this.strings[RANGS[m.c] ?? 'match_secondary'])}
      ${this.lectureHtml(ref)}`;
  }

  /** Le parfum a sa création Révèle, mais elle n'est pas encore en vente. */
  afficherBientot(ref) {
    const cartes = this.cartesDe(ref.m);
    const titre = ref.soon?.t ?? '';

    this.body.innerHTML = `
      <div class="rv-finder__notice rv-finder__notice--soon">
        <p class="rv-finder__notice-eyebrow">${this.esc(this.strings.tagSoon)}</p>
        <p class="rv-finder__notice-title">
          ${this.esc(this.strings.soonTitle.replace('__NAME__', ref.n).replace('__TITLE__', titre))}
        </p>
        <p class="rv-finder__notice-text">${this.esc(this.strings.soonText)}</p>
        ${this.formulaireAlerteHtml(ref.soon?.h, titre)}
      </div>

      <p class="rv-finder__section-title">${this.strings.meanwhile}</p>
      ${this.grilleHtml(cartes, (m, i) => (i === 0 ? this.strings.rankClosest : this.strings.rankAlso), true)}
      ${this.lectureHtml(ref)}`;
  }

  /** Parfum connu, absent de la collection : les créations disponibles les plus proches. */
  afficherHorsCatalogue(ref) {
    const cartes = this.cartesDe(ref.m);
    const loin = !cartes.length || cartes[0].s < 55;

    this.body.innerHTML = `
      <div class="rv-finder__notice">
        <p class="rv-finder__notice-title">${this.esc(this.strings.extTitle.replace('__NAME__', ref.n))}</p>
        <p class="rv-finder__notice-text">${this.esc(this.strings.extText)}</p>
      </div>

      <p class="rv-finder__section-title">${this.strings.ourMatches}</p>
      ${this.grilleHtml(cartes, (m, i) => (i === 0 ? this.strings.rankClosest : this.strings.rankAlso), true)}
      ${
        loin
          ? `<div class="rv-finder__notice rv-finder__notice--quiet">
               <p class="rv-finder__notice-text">${this.esc(this.strings.extFar)}</p>
               ${this.boutonQuizHtml(this.strings.refine, true)}
             </div>`
          : ''
      }
      ${this.lectureHtml(ref)}`;
  }

  /**
   * Une proximité faible dessert plus qu'elle ne sert : au-delà de la première carte,
   * on ne montre que les créations vraiment proches.
   */
  cartesDe(correspondances) {
    return (correspondances ?? [])
      .filter((m, i) => i === 0 || m.s == null || m.s >= 50)
      .slice(0, 3)
      .map((m) => ({ ...m, produit: this.index.products[m.h] }))
      .filter((m) => m.produit);
  }

  /**
   * Grille de une à trois cartes. `libelle(m, i)` fournit la mention de rang ;
   * `affinites` affiche le score de proximité sous forme de pourcentage.
   */
  grilleHtml(cartes, libelle, affinites = false) {
    if (!cartes.length) return '';
    return `<div class="rv-finder__matches rv-finder__matches--${cartes.length}">
      ${cartes.map((m, i) => this.carteHtml(m, libelle(m, i), affinites ? m.s : null, m.pourquoi)).join('')}
    </div>`;
  }

  lectureHtml(ref) {
    const notes = ref.nx
      ? [[this.strings.notesMain, ref.nx]]
      : [
          [this.strings.notesTop, ref.nt],
          [this.strings.notesHeart, ref.nc],
          [this.strings.notesBase, ref.nf],
        ];

    return `
      <div class="rv-finder__reference">
        <p class="rv-finder__section-title">${this.strings.ourReading}</p>
        <p class="rv-finder__reference-name">${this.esc(ref.n)}</p>
        ${ref.f ? `<p class="rv-finder__reference-dna">${this.esc(ref.f)}</p>` : ''}
        <div class="rv-finder__notes">
          ${notes
            .filter(([, v]) => v?.length)
            .map(
              ([label, valeurs]) => `
            <div class="rv-finder__note-row">
              <span class="rv-finder__note-label">${this.esc(label)}</span>
              <span class="rv-finder__note-value">${this.esc(valeurs.join(', '))}</span>
            </div>`
            )
            .join('')}
        </div>
      </div>`;
  }

  carteHtml(m, rang, score, pourquoi) {
    const p = m.produit;
    const prix = (p.p / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

    return `
      <article class="rv-finder__match">
        <div class="rv-finder__match-media">
          ${p.img ? `<img src="${this.esc(p.img)}" alt="" loading="lazy" width="200" height="290">` : ''}
        </div>
        <div class="rv-finder__match-body">
          <p class="rv-finder__match-rank">${this.esc(rang)}</p>
          <p class="rv-finder__match-name"><a href="${this.esc(p.u)}">${this.esc(p.t)}</a></p>
          ${score != null ? `<p class="rv-finder__affinity">${this.esc(this.strings.affinity.replace('__PCT__', score))}</p>` : ''}
          ${pourquoi ? `<p class="rv-finder__why">${this.esc(pourquoi)}</p>` : ''}
          <p class="rv-finder__match-price">${prix} <span aria-hidden="true">·</span> ${this.esc(p.v)}</p>
          <button type="button" class="rv-finder__match-add"
                  data-handle="${this.esc(m.h)}" ${p.av ? '' : 'disabled'}>
            ${p.av ? this.esc(this.strings.addToCart) : this.esc(this.strings.soldOut)}
          </button>
        </div>
      </article>`;
  }

  /* ── Alerte de sortie ───────────────────────────────────────────────── */

  formulaireAlerteHtml(handle, titre) {
    if (!handle) return '';
    const id = `rv-finder-notify-${handle}`;
    return `
      <form class="rv-finder__notify" data-notify-handle="${this.esc(handle)}" data-notify-title="${this.esc(titre)}" novalidate>
        <label class="rv-sr-only" for="${id}">${this.esc(this.strings.notifyLabel)}</label>
        <div class="rv-finder__notify-row">
          <input class="rv-finder__notify-input" id="${id}" type="email" name="email"
                 autocomplete="email" inputmode="email" required
                 placeholder="${this.esc(this.strings.notifyLabel)}">
          <button type="submit" class="rv-finder__notify-submit">${this.esc(this.strings.notifySubmit)}</button>
        </div>
        <p class="rv-finder__notify-error" role="alert" hidden></p>
        <p class="rv-finder__notify-consent">${this.esc(this.strings.notifyConsent)}</p>
      </form>`;
  }

  async onEnvoiAlerte(e) {
    const form = e.target.closest('.rv-finder__notify');
    if (!form) return;
    e.preventDefault();

    const champ = form.querySelector('input[type="email"]');
    const erreur = form.querySelector('.rv-finder__notify-error');
    const bouton = form.querySelector('button[type="submit"]');
    const email = champ.value.trim();

    // Volontairement permissif : on rejette l'évidemment faux, pas l'inhabituel
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      erreur.textContent = this.strings.notifyInvalid;
      erreur.hidden = false;
      champ.setAttribute('aria-invalid', 'true');
      champ.focus();
      return;
    }

    erreur.hidden = true;
    champ.removeAttribute('aria-invalid');
    bouton.disabled = true;
    bouton.setAttribute('aria-busy', 'true');

    const handle = form.dataset.notifyHandle;
    try {
      await inscrireAlerte(email, handle, {
        titre: form.dataset.notifyTitle,
        recherche: this.input.value,
        source: 'revelateur',
        url: `${location.origin}/products/${handle}`,
      });
      this.emettre('rv_finder_notify', { handle });
      form.outerHTML = `<p class="rv-finder__notify-success" role="status">${this.esc(
        this.strings.notifySuccess.replace('__TITLE__', form.dataset.notifyTitle)
      )}</p>`;
    } catch (err) {
      console.error('[Révèle] Alerte de sortie :', err);
      erreur.textContent = this.strings.notifyError;
      erreur.hidden = false;
      bouton.disabled = false;
      bouton.removeAttribute('aria-busy');
    }
  }

  /* ── Questionnaire ──────────────────────────────────────────────────── */

  demarrerQuiz() {
    this.input.value = '';
    this.clear.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.suggestions = [];
    this.quiz = { etape: 0, reponses: Object.fromEntries(QUESTIONS.map((q) => [q.id, []])) };
    this.emettre('rv_matchmaker_start');
    this.afficherQuestion();
  }

  afficherQuestion() {
    const q = QUESTIONS[this.quiz.etape];
    const choisis = this.quiz.reponses[q.id];
    const s = this.strings.quiz;
    const total = QUESTIONS.length;
    const derniere = this.quiz.etape === total - 1;
    const pret = choisis.length >= q.min;
    const multiple = q.max > 1;

    this.body.innerHTML = `
      <div class="rv-quiz">
        <div class="rv-quiz__progress" aria-hidden="true">
          <span style="width:${((this.quiz.etape + 1) / total) * 100}%"></span>
        </div>
        <p class="rv-quiz__step">${this.esc(s.progress.replace('__N__', this.quiz.etape + 1).replace('__TOTAL__', total))}</p>
        <p class="rv-quiz__question" id="rv-quiz-q">${this.esc(s[`q_${q.id}`])}</p>
        <p class="rv-quiz__help">${this.esc(s[q.aide])}</p>

        <div class="rv-quiz__options" role="group" aria-labelledby="rv-quiz-q">
          ${q.options
            // Ne pas proposer d'écarter la famille que le client vient de choisir
            .filter((o) => !(q.id === 'eviter' && this.quiz.reponses.univers.some((u) => FAMILLE_DE[u] === EXCLUSION_DE[o])))
            .map((o) => {
              const actif = choisis.includes(o);
              const detail = s[`d_${o}`];
              return `<button type="button" class="rv-quiz__option" data-quiz-option="${o}"
                        aria-pressed="${actif}" ${multiple ? '' : 'data-quiz-single'}>
                <span class="rv-quiz__option-label">${this.esc(s[`o_${o}`])}</span>
                ${detail ? `<span class="rv-quiz__option-detail">${this.esc(detail)}</span>` : ''}
              </button>`;
            })
            .join('')}
        </div>

        <div class="rv-quiz__nav">
          ${
            this.quiz.etape > 0
              ? `<button type="button" class="rv-quiz__back" data-quiz-back>${this.esc(s.back)}</button>`
              : `<button type="button" class="rv-quiz__back" data-quiz-byname>${this.esc(s.by_name)}</button>`
          }
          <button type="button" class="rv-finder__cta" data-quiz-next ${pret ? '' : 'disabled'}>
            ${this.esc(derniere ? s.see : s.next)}
          </button>
        </div>
      </div>`;

    requestAnimationFrame(() => this.body.querySelector('.rv-quiz__option')?.focus());
    this.body.scrollTop = 0;
  }

  choisirOption(option) {
    const q = QUESTIONS[this.quiz.etape];
    let choisis = this.quiz.reponses[q.id];

    if (choisis.includes(option)) {
      choisis = choisis.filter((c) => c !== option);
    } else if (q.max === 1) {
      choisis = [option];
    } else if (q.exclusive && option === q.exclusive) {
      choisis = [option];
    } else {
      choisis = choisis.filter((c) => c !== q.exclusive);
      // Au-delà du maximum, le plus ancien choix cède sa place au nouveau
      if (choisis.length >= q.max) choisis = choisis.slice(1);
      choisis = [...choisis, option];
    }

    this.quiz.reponses[q.id] = choisis;

    // Une question à choix unique avance d'elle-même : un clic de moins
    if (q.max === 1 && this.quiz.etape < QUESTIONS.length - 1) {
      this.quiz.etape += 1;
      this.afficherQuestion();
      return;
    }

    const focusOption = option;
    this.afficherQuestion();
    requestAnimationFrame(() => this.body.querySelector(`[data-quiz-option="${focusOption}"]`)?.focus());
  }

  /**
   * Calcule la sélection : de un à trois parfums DISPONIBLES. Un deuxième ou un
   * troisième parfum n'est proposé que s'il reste proche du premier ; mieux vaut un
   * seul conseil franc que trois propositions dont deux au hasard.
   */
  afficherSelection() {
    const rep = this.quiz.reponses;
    const s = this.strings.quiz;

    const exclues = this.exclusionsUtiles(rep).map((e) => EXCLUSION_DE[e]);
    const disponibles = Object.entries(this.index.products)
      .filter(([, p]) => p.av)
      .map(([h, p]) => ({ h, produit: p, s: affinite(p, rep) }));

    let candidats = disponibles.filter((c) => !exclues.includes(c.produit.f));
    const assoupli = !candidats.length && disponibles.length > 0;
    if (assoupli) candidats = disponibles;

    candidats.sort((a, b) => b.s - a.s);
    const tete = candidats[0];
    const selection = tete ? [tete] : [];
    for (const c of candidats.slice(1)) {
      if (selection.length >= 3) break;
      if (c.s >= 60 && c.s >= tete.s - 12) selection.push(c);
    }

    for (const c of selection) c.pourquoi = this.raisons(c.produit, rep);

    this.emettre('rv_matchmaker_result', {
      reponses: rep,
      handles: selection.map((c) => c.h),
      scores: selection.map((c) => c.s),
    });

    const profil = [
      ...rep.univers.map((u) => s[`o_${u}`]),
      ...rep.moment.map((m) => s[`o_${m}`]),
      s[`o_${rep.presence[0]}`],
    ]
      .filter(Boolean)
      .join(' · ');

    const pack =
      selection.length === 2 ? s.pack_duo : selection.length === 3 ? s.pack_trio : null;

    this.body.innerHTML = `
      <div class="rv-quiz rv-quiz--result">
        <p class="rv-finder__section-title">${this.esc(s.result_title)}</p>
        <p class="rv-quiz__profile">${this.esc(profil)}</p>
        <p class="rv-quiz__help">${this.esc(
          selection.length > 1 ? s.result_many.replace('__N__', selection.length) : s.result_one
        )}</p>
        ${assoupli ? `<p class="rv-quiz__help">${this.esc(s.relaxed)}</p>` : ''}

        ${this.grilleHtml(
          selection.map((c) => ({ ...c, c: 'secondary' })),
          (m, i) => (i === 0 ? this.strings.rankClosest : this.strings.rankAlso),
          true
        )}

        ${
          pack
            ? `<button type="button" class="rv-finder__cta rv-quiz__pack"
                       data-pack="${this.esc(selection.map((c) => c.h).join(','))}">${this.esc(pack)}</button>`
            : ''
        }

        <div class="rv-quiz__nav">
          <button type="button" class="rv-quiz__back" data-quiz-start>${this.esc(s.restart)}</button>
          <button type="button" class="rv-quiz__back" data-quiz-byname>${this.esc(s.by_name)}</button>
        </div>
      </div>`;
    this.body.scrollTop = 0;
  }

  /**
   * Une exclusion qui contredit l'univers choisi (« le bouquet » puis « trop fleuri »)
   * est ignorée : le client veut un floral léger, pas l'absence de floral.
   */
  exclusionsUtiles(rep) {
    const familles = rep.univers.map((u) => FAMILLE_DE[u]);
    return rep.eviter.filter((e) => EXCLUSION_DE[e] && !familles.includes(EXCLUSION_DE[e]));
  }

  /** Une phrase courte qui dit pourquoi ce parfum a été retenu. */
  raisons(produit, rep) {
    const s = this.strings.quiz;
    const morceaux = [];
    const familles = rep.univers.map((u) => FAMILLE_DE[u]);
    if (familles.includes(produit.f)) morceaux.push(s.why_family.replace('__F__', produit.f.toLowerCase()));
    else if (produit.f) morceaux.push(s.why_near.replace('__F__', produit.f.toLowerCase()));

    const voulus = rep.moment.flatMap((m) => MOMENTS_DE[m]);
    const commun = (produit.mo ?? []).find((m) => voulus.includes(m));
    if (commun) morceaux.push(commun.toLowerCase());

    if (produit.si) morceaux.push(s.why_sillage.replace('__S__', produit.si.toLowerCase()));
    return morceaux.slice(0, 3).join(' · ');
  }

  /* ── Interactions du corps ──────────────────────────────────────────── */

  onClicCorps(e) {
    const sugg = e.target.closest('[data-ref]');
    if (sugg) {
      this.selectionner(sugg.dataset.ref);
      return;
    }

    if (e.target.closest('[data-quiz-start]')) {
      this.demarrerQuiz();
      return;
    }

    if (e.target.closest('[data-quiz-byname]')) {
      this.quiz = null;
      this.afficherAccueil();
      this.input.focus();
      return;
    }

    const option = e.target.closest('[data-quiz-option]');
    if (option && this.quiz) {
      this.choisirOption(option.dataset.quizOption);
      return;
    }

    if (e.target.closest('[data-quiz-back]') && this.quiz) {
      this.quiz.etape = Math.max(0, this.quiz.etape - 1);
      this.afficherQuestion();
      return;
    }

    const suivant = e.target.closest('[data-quiz-next]');
    if (suivant && !suivant.disabled && this.quiz) {
      if (this.quiz.etape < QUESTIONS.length - 1) {
        this.quiz.etape += 1;
        this.afficherQuestion();
      } else {
        this.afficherSelection();
      }
      return;
    }

    const pack = e.target.closest('[data-pack]');
    if (pack && !pack.disabled) {
      this.ajouterPack(pack);
      return;
    }

    const famille = e.target.closest('[data-famille]');
    if (famille) {
      window.location.href = `/collections/${this.slug(famille.dataset.famille)}`;
      return;
    }

    const ajout = e.target.closest('[data-handle]');
    if (ajout && !ajout.disabled) {
      this.ajouterAuPanier(ajout);
    }
  }

  /* ── Panier ─────────────────────────────────────────────────────────── */

  /**
   * Résout la variante vendable au moment du clic plutôt que de la figer dans
   * l'index : le handle est stable, l'identifiant de variante non.
   */
  async varianteVendable(handle) {
    const produit = await (await fetch(`/products/${handle}.js`)).json();
    const variante = produit.variants.find((v) => v.available) ?? produit.variants[0];
    if (!variante?.available) throw new Error(`${handle} : aucune variante disponible`);
    return variante.id;
  }

  async ajouterAuPanier(bouton) {
    const handle = bouton.dataset.handle;
    await this.envoyerAuPanier(bouton, [handle], this.strings.added, () => {
      this.emettre('rv_finder_add_to_cart', { handle });
    });
  }

  /** Ajoute toute la sélection du questionnaire d'un seul geste. */
  async ajouterPack(bouton) {
    const handles = bouton.dataset.pack.split(',');
    await this.envoyerAuPanier(bouton, handles, this.strings.quiz.pack_added, () => {
      this.emettre('rv_finder_add_to_cart', { handles, source: 'matchmaker' });
    });
  }

  async envoyerAuPanier(bouton, handles, libelleSucces, apres) {
    const libelleInitial = bouton.textContent;
    bouton.setAttribute('aria-busy', 'true');
    bouton.disabled = true;
    bouton.textContent = this.strings.adding;

    try {
      const ids = await Promise.all(handles.map((h) => this.varianteVendable(h)));
      const reponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
      });
      if (!reponse.ok) throw new Error(`cart/add ${reponse.status}`);

      bouton.textContent = libelleSucces;
      apres?.();
      this.rafraichirPanier();

      setTimeout(() => {
        bouton.textContent = libelleInitial;
        bouton.disabled = false;
        bouton.removeAttribute('aria-busy');
      }, 2000);
    } catch (err) {
      console.error('[Révèle] Ajout au panier :', err);
      bouton.textContent = this.strings.addError;
      setTimeout(() => {
        bouton.textContent = libelleInitial;
        bouton.disabled = false;
        bouton.removeAttribute('aria-busy');
      }, 2500);
    }
  }

  /** Met à jour le compteur, le tiroir de Dawn et la barre de composition du pack. */
  rafraichirPanier() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.onCartUpdate) drawer.onCartUpdate();
  }

  /* ── Utilitaires ────────────────────────────────────────────────────── */

  esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  slug(s) {
    return normaliser(s).replace(/\s+/g, '-');
  }

  emettre(nom, detail = {}) {
    document.dispatchEvent(new CustomEvent(`revele:${nom}`, { bubbles: true, detail }));
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({ event: nom, ...detail });
    }
  }
}

/**
 * Inscription à l'alerte de sortie d'une création.
 *
 * Envoie l'évènement « Alerte sortie Révèle » à Klaviyo par son API client, avec la
 * clé PUBLIQUE du site (la même que celle du script Klaviyo, sans risque à exposer).
 * Le profil est créé ou complété ; un flow Klaviyo déclenché par cet évènement et
 * filtré sur `handle` prévient le client le jour de la sortie
 * (docs/05-POPUP-KLAVIYO.md §7).
 *
 * Pourquoi pas le formulaire client de Shopify : il exige un jeton anti-robot que
 * seul un envoi natif de page obtient — un `fetch` reçoit « Missing CAPTCHA token ».
 */
async function inscrireAlerte(email, handle, details = {}) {
  const cle = document.querySelector('revele-finder')?.dataset.klaviyo;
  if (!cle) throw new Error('clé publique Klaviyo absente');

  const reponse = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(cle)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
    body: JSON.stringify({
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: 'Alerte sortie Révèle' } } },
          profile: { data: { type: 'profile', attributes: { email } } },
          properties: { handle, ...details },
          unique_id: `${handle}-${email}`,
        },
      },
    }),
  });

  if (!reponse.ok) throw new Error(`Klaviyo ${reponse.status}`);
}

window.ReveleInscrireAlerte = inscrireAlerte;

if (!customElements.get('revele-finder')) {
  customElements.define('revele-finder', ReveleFinder);
}
